import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  Menu,
  X,
  Search,
  Clock,
  CreditCard,
  FileText,
  Code2,
  LifeBuoy,
  ChevronDown,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/motion/Reveal";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";

const WA_BASE =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%20ho%20una%20domanda%20che%20non%20ho%20trovato%20nelle%20FAQ%3A%20";

const WA_URL = WA_BASE + encodeURIComponent("___");

type FaqItem = { q: string; a: string };
type FaqCategory = { id: string; title: string; Icon: typeof Clock; items: FaqItem[] };

const CATEGORIES: FaqCategory[] = [
  {
    id: "tempi",
    title: "Tempi e consegna",
    Icon: Clock,
    items: [
      {
        q: "Le 48 ore sono davvero garantite?",
        a: "Sì. Contiamo le 48 ore lavorative dal momento in cui riceviamo il briefing completo (testi, foto, referenze). Se non rispettiamo la scadenza concordata per causa nostra, ti riconosciamo uno sconto sul progetto o il rimborso proporzionale della parte non consegnata. La policy esatta è scritta nell'offerta che firmiamo — nessuna clausola nascosta.",
      },
      {
        q: "Cosa succede se non rispondo in tempo al briefing?",
        a: "Nessun problema, il conteggio delle 48h si ferma finché non riceviamo tutto il materiale. Ti mandiamo un promemoria dopo 3 giorni e restiamo disponibili per aiutarti a raccogliere i contenuti mancanti — molti clienti hanno bisogno di una mano per i testi, ed è normale.",
      },
      {
        q: "Posso vedere il sito prima che sia online?",
        a: "Sempre. A metà lavorazione ricevi un link privato su un dominio temporaneo (es. cliente.flashweb-preview.ch) protetto da password. Puoi navigarlo da mobile e desktop, condividerlo con soci o famiglia e mandarci feedback prima della pubblicazione ufficiale.",
      },
    ],
  },
  {
    id: "prezzi",
    title: "Prezzi e pagamenti",
    Icon: CreditCard,
    items: [
      {
        q: "Il prezzo indicato è tutto incluso o ci sono costi nascosti?",
        a: "Il prezzo che vedi è quello che paghi. Sono inclusi: design, sviluppo, primo anno di hosting, primo anno di dominio .ch/.com, certificato SSL, SEO on-page, 30 giorni di assistenza post-lancio. Non sono inclusi solo servizi extra che chiedi tu (es. copywriting professionale, fotografia, integrazioni custom con software terzi) e sempre solo dopo un preventivo scritto approvato.",
      },
      {
        q: "Come funziona il pagamento — anticipato, a rate, alla consegna?",
        a: "Sui piani una tantum (Sito Professionale CHF 990 e Sito Completo CHF 1'790): 50% all'accettazione del preventivo, 50% alla consegna del sito online. Su FlashWeb Flex: nessun anticipo, primo mese pagato alla firma e poi CHF 59 al mese con addebito automatico. Accettiamo bonifico, TWINT e carta.",
      },
      {
        q: "Posso passare da FlashWeb Flex a un piano una tantum, o viceversa?",
        a: "Sì, in entrambi i sensi. Da Flex a una tantum: scomputiamo dal prezzo del sito i mesi già pagati (fino a 6 mesi). Da una tantum a Flex: attivi l'abbonamento e assorbiamo il tuo sito nella manutenzione senza rifarlo. Basta scriverci su WhatsApp, gestiamo il passaggio in 24h.",
      },
      {
        q: "Cosa succede se disdico FlashWeb Flex — perdo il sito?",
        a: "Puoi disdire in qualsiasi momento con preavviso di 30 giorni. Alla scadenza hai due opzioni: (1) riscattare il sito con un pagamento una tantum agevolato (calcolato in base ai mesi pagati) e portarlo dove vuoi; (2) chiudere tutto e ti forniamo un backup completo del sito in formato standard. Non trattieni ostaggi digitali: è tuo dal giorno uno.",
      },
    ],
  },
  {
    id: "contenuti",
    title: "Contenuti e revisioni",
    Icon: FileText,
    items: [
      {
        q: "Quante revisioni sono incluse?",
        a: "Sito Professionale: 1 tornata di revisioni (fino a 10 modifiche). Sito Completo: 2 tornate (fino a 20 modifiche totali). FlashWeb Flex: revisioni illimitate — l'unico limite è il buon senso. Se una revisione richiede più di 4h di lavoro, te lo comunichiamo prima e decidiamo insieme.",
      },
      {
        q: "Devo fornire io i testi e le foto, o li scrivete voi?",
        a: "Idealmente sì: nessuno conosce la tua attività meglio di te. Se però i testi non li hai, offriamo un servizio di copywriting professionale a CHF 90/pagina e un pacchetto foto stock premium a CHF 150. Per la fotografia originale ti mettiamo in contatto con fotografi partner in Ticino e a Zurigo (prezzo diretto, senza margine).",
      },
      {
        q: "Posso modificare il sito da solo dopo la consegna?",
        a: "Sul Sito Completo e su Flex hai accesso alla dashboard cliente da cui modifichi testi, immagini, orari, prezzi in autonomia — senza toccare codice. Sul Sito Professionale le modifiche testuali sono a pagamento (CHF 30/modifica) o gratuite se attivi FlashWeb Flex per la manutenzione.",
      },
    ],
  },
  {
    id: "tecnico",
    title: "Tecnico e proprietà",
    Icon: Code2,
    items: [
      {
        q: "Il codice e il dominio sono miei?",
        a: "Sì, sempre. Il dominio è intestato alla tua azienda dal giorno uno (te lo dimostriamo con la ricevuta di registrazione). Il codice sorgente è tuo sui piani una tantum al saldo del pagamento: ti consegniamo un archivio completo del progetto. Su Flex il codice resta sulla nostra infrastruttura finché l'abbonamento è attivo, ma puoi riscattarlo in qualsiasi momento.",
      },
      {
        q: "Su quale tecnologia costruite i siti (WordPress, custom, no-code)?",
        a: "Costruiamo con stack moderno: React + TanStack Start, Tailwind CSS, deploy su Cloudflare o Vercel. Zero WordPress, zero plugin da aggiornare, zero rischi di sicurezza da tema scaduto. Il risultato è un sito 5-10× più veloce di un WordPress equivalente, con punteggio Lighthouse 95+ e SEO tecnico impeccabile.",
      },
      {
        q: "Il sito è ottimizzato per Google (SEO)? Cosa include esattamente?",
        a: "Ogni sito include: meta tag e Open Graph per pagina, sitemap.xml, robots.txt, structured data JSON-LD (Organization, LocalBusiness, Article dove serve), URL parlanti, immagini ottimizzate WebP, lazy loading, mobile-first, HTTPS. Sul Sito Completo aggiungiamo ricerca keyword iniziale e ottimizzazione copy per i termini principali. Non promettiamo posizioni: chi lo fa mente.",
      },
      {
        q: "Cosa succede se voglio cambiare agenzia in futuro — posso portarmi via il sito?",
        a: "Assolutamente sì. Il codice è standard React/HTML/CSS: qualsiasi sviluppatore competente può prenderlo in mano. Ti forniamo repository Git, credenziali hosting/dominio e documentazione tecnica del progetto. Non usiamo lock-in tecnologici né page-builder proprietari.",
      },
    ],
  },
  {
    id: "dopo",
    title: "Dopo il lancio",
    Icon: LifeBuoy,
    items: [
      {
        q: "Cosa copre l'assistenza inclusa e per quanto tempo?",
        a: "Sui piani una tantum: 30 giorni di assistenza post-lancio inclusi che coprono correzione bug, piccole modifiche testuali (fino a 5), supporto tecnico via email e WhatsApp con risposta entro 4h lavorative. Su Flex: assistenza illimitata per tutta la durata dell'abbonamento, senza scadenza.",
      },
      {
        q: "Come segnalo un problema o chiedo una modifica?",
        a: "Tre canali, scegli il più comodo: (1) Portale Assistenza su /portal — apri un ticket con screenshot; (2) WhatsApp diretto a Filippo per urgenze; (3) email a info@flash-web.ch. Rispondiamo entro 4h lavorative dal lunedì al venerdì. Per emergenze critiche (sito offline) rispondiamo anche nel weekend.",
      },
      {
        q: "Fate manutenzione anche su siti non costruiti da voi?",
        a: "Sì, con FlashWeb Flex Rescue (CHF 79/mese). Facciamo prima un audit tecnico gratuito del tuo sito attuale, ti diciamo onestamente se vale la pena mantenerlo o se conviene rifarlo, e in base a questo decidiamo insieme. Non prendiamo in carico siti WordPress con più di 15 plugin: sono ingestibili nel lungo periodo.",
      },
    ],
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Domande frequenti | FlashWeb Agency" },
      {
        name: "description",
        content:
          "Tempi, garanzie, pagamenti, proprietà del codice, revisioni incluse: tutte le risposte prima di iniziare il tuo progetto con FlashWeb.",
      },
      { property: "og:title", content: "Domande frequenti — FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "Tutte le risposte su tempi, garanzie e pagamenti prima di iniziare il tuo progetto con FlashWeb.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/faq" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: CATEGORIES.flatMap((c) =>
            c.items.map((it) => ({
              "@type": "Question",
              name: it.q,
              acceptedAnswer: { "@type": "Answer", text: it.a },
            }))
          ),
        }),
      },
          {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "FAQ", item: "https://www.flash-web.ch/faq" },
          ],
        }),
      },
],
  }),
  component: FaqPage,
});

// ─── Navbar (matches other pages) ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} — Tutti i diritti riservati.</p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-semibold text-foreground">Bareerstrasse 75</span><br />6300 Zug, Svizzera</p>
              <p><span className="font-semibold text-foreground">Via Ai Prati 2</span><br />Bedano, Svizzera</p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a></p>
              <p><a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a></p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">N° registro commercio:<br /><span className="font-semibold text-foreground">CH-170.4.022.832-9</span></p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Accordion item ───
function FaqRow({
  item,
  isOpen,
  onToggle,
}: {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={`rounded-2xl border bg-card/50 backdrop-blur transition-colors ${
        isOpen ? "border-primary/50" : "border-border"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
      >
        <span className="font-display text-base font-semibold text-foreground sm:text-lg">
          {item.q}
        </span>
        <span
          className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border text-primary transition-all ${
            isOpen ? "rotate-180 border-primary/60 bg-primary/10" : "border-border"
          }`}
        >
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>
      <div
        className={`grid overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="min-h-0">
          <div className="border-t border-border/60 px-5 py-5 text-sm leading-relaxed text-muted-foreground sm:px-6 sm:py-6 sm:text-base">
            {item.a}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════ PAGE ═══════════════
function FaqPage() {
  const [query, setQuery] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIES;
    return CATEGORIES.map((c) => ({
      ...c,
      items: c.items.filter(
        (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
      ),
    })).filter((c) => c.items.length > 0);
  }, [query]);

  const totalMatches = filtered.reduce((n, c) => n + c.items.length, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <HelpCircle className="h-3 w-3" />
              Domande frequenti
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl md:text-6xl">
              Prima di scriverci, <span className="text-gradient">probabilmente ti stai chiedendo...</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Tempi, garanzie, pagamenti, codice sorgente, assistenza. Cerca la tua domanda
              o esplora per categoria.
            </p>
          </Reveal>

          {/* Search */}
          <Reveal delay={0.3}>
            <div className="mx-auto mt-10 max-w-xl">
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca una domanda… (es. rimborso, dominio, revisioni)"
                  className="h-12 w-full rounded-2xl border border-border bg-card/60 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground backdrop-blur transition-colors focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  aria-label="Cerca nelle FAQ"
                />
              </div>
              {query && (
                <p className="mt-3 text-xs text-muted-foreground">
                  {totalMatches === 0
                    ? "Nessuna risposta trovata. Scrivici direttamente su WhatsApp."
                    : `${totalMatches} risposta${totalMatches === 1 ? "" : "e"} trovata${
                        totalMatches === 1 ? "" : "e"
                      }.`}
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative mx-auto w-full max-w-4xl px-6 py-16 sm:py-20">
        <div className="space-y-14">
          {filtered.map((cat) => (
            <div key={cat.id} id={cat.id}>
              <Reveal>
                <div className="mb-6 flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
                    <cat.Icon className="h-5 w-5" />
                  </span>
                  <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                    {cat.title}
                  </h2>
                </div>
              </Reveal>
              <div className="space-y-3">
                {cat.items.map((item, i) => {
                  const key = `${cat.id}-${i}`;
                  return (
                    <Reveal key={key} delay={i * 0.04}>
                      <FaqRow
                        item={item}
                        isOpen={openKey === key}
                        onToggle={() =>
                          setOpenKey((prev) => (prev === key ? null : key))
                        }
                      />
                    </Reveal>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
              <MessageCircle className="h-3 w-3" />
              Non hai trovato risposta?
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Scrivici direttamente. <span className="text-gradient">Rispondiamo in giornata.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Ogni domanda che non trovi qui è materiale per la prossima FAQ. Scrivici su
              WhatsApp: rispondiamo entro 4 ore lavorative, sempre una persona reale.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Fai la tua domanda
                </a>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <Link to="/chi-siamo">
                  Conosci il team <ArrowRight />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
